import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const category = article
    ? categories.find((c) => c.slug === article.categorySlug)
    : null;
  const relatedArticles = article
    ? articles
        .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
        .slice(0, 3)
    : [];

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-foreground-300">
              <i className="ri-article-line text-4xl"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">
              文章未找到
            </h1>
            <p className="text-foreground-500 text-sm mb-4">该文章不存在或已被删除</p>
            <Link
              to="/"
              className="px-6 py-2.5 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-sm inline-flex items-center gap-2"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </div>
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishedAt,
    dateModified: article.publishedAt,
    publisher: {
      '@type': 'Organization',
      name: '黑料入口',
    },
    url: `https://www.heiliaorukou.com/article/${article.slug}`,
    image: article.imageUrl,
    keywords: article.tags.join(','),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://www.heiliaorukou.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.category,
        item: `https://www.heiliaorukou.com/category/${article.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 黑料入口`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.heiliaorukou.com/article/${article.slug}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogImage={article.imageUrl}
        articlePublishedTime={article.publishedAt}
        articleModifiedTime={article.publishedAt}
        articleTags={article.tags}
        jsonLd={[articleJsonLd, breadcrumbJsonLd]}
      />

      <div className="min-h-screen">
        <Navbar />
        <main className="pt-20">
          <article className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: article.category, href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />

            <header className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="text-xs px-2.5 py-1 bg-accent-100 text-accent-700 rounded-md font-medium cursor-pointer hover:bg-accent-200 transition-colors"
                >
                  {article.category}
                </Link>
                {article.isHot && (
                  <span className="text-xs px-2.5 py-1 bg-primary-100 text-primary-700 rounded-md font-medium flex items-center gap-1">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-fire-fill text-[10px]"></i>
                    </div>
                    热门
                  </span>
                )}
              </div>

              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-user-3-line"></i>
                  </div>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line"></i>
                  </div>
                  {article.publishedAt}
                </span>
                <span className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line"></i>
                  </div>
                  {article.views.toLocaleString()} 次阅读
                </span>
                <span className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-chat-3-line"></i>
                  </div>
                  {article.comments.toLocaleString()} 条评论
                </span>
              </div>
            </header>

            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-10">
              <img
                src={article.imageUrl}
                alt={article.imageAlt}
                title={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div
              className="article-content pb-10"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="border-t border-background-200/70 pt-6 pb-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2.5 py-1 bg-background-100 text-foreground-600 rounded-md"
                  >
                    # {tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="max-w-4xl mx-auto px-4 md:px-6 pb-14">
              <div className="border-t border-background-200/70 pt-10">
                <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6 flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center text-primary-500">
                    <i className="ri-links-line"></i>
                  </div>
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <ArticleCard key={ra.id} article={ra} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}